import styled from 'styled-components';
import ThemeV2 from '../../../theme';
import { Flex } from '../../../Utils/styles/display';
import { windowSizeAdapter } from '../../../Utils/window';
import CircularCheckbox from '../../../atomic/atoms/Checkbox/CircularCheckbox';
import { TaskCreationPosition } from './types';
import Body from '../../../atomic/atoms/Body';
import SVGIcon from '../../../atomic/atoms/SVGIcon';
import NewTextfield from '../../../atomic/molecules/NewTextfield';
import { StyledDropdownIconButton } from '../../../atomic/organism/Notebook/TaskItem/styles';
import Tooltip from '../../../atomic/molecules/ToolTipV2';
import InfiniteScroll from 'react-infinite-scroll-component';

export const BoardTitle = styled(Flex)<{
  background?: string;
  hasAddButton?: boolean;
}>`
  padding: ${({ hasAddButton }) =>
    hasAddButton ? '8px 0 8px 8px' : '16px 0 16px 16px'};
  border-radius: 4px;
  background: ${({ background }) =>
    background ? background : ThemeV2.palette.gray3};
  flex-flow: row nowrap;
  height: 56px;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 12px 0 12px;
  max-width: 300px;
  min-width: 300px;
`;

export const DummyContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 12px 0 12px;
  min-width: 272px;
`;

export const ScrollContainer = styled.div<{
  hasRoundedBorder?: boolean;
  isDraggingOver?: boolean;
}>`
  background: ${ThemeV2.palette.gray3};
  display: flex;
  ${({ hasRoundedBorder }) =>
    `${
      hasRoundedBorder ? 'border-radius: 4px' : 'border-radius: 4px 4px 0 0'
    }`};
  flex-direction: column;
  padding: 0 12px;

  transition: background-color 0.2s ease, opacity 0.1s ease;
  user-select: none;
  position: relative;
`;

export const Wrapper = styled.div`
  margin-top: 8px;
  padding: 12px 0;
  background: ${ThemeV2.palette.gray3};
`;

export const SectionContainer = styled.div<{
  isMobileApp: boolean;
  hasAddButton?: boolean;
  isEmbeddedInMainApp?: boolean;
}>`
  border-top-radius: 4px;
  .infinite-scroll-component {
    max-height: ${({ isMobileApp, hasAddButton, isEmbeddedInMainApp }) =>
      windowSizeAdapter(isMobileApp, {
        mobileApp: hasAddButton
          ? `calc(var(--mobileVh, 1vh) * 100 - 290px + ${
              isEmbeddedInMainApp ? '80px' : 0
            })`
          : `calc(var(--mobileVh, 1vh) * 100 - 230px + ${
              isEmbeddedInMainApp ? '80px' : 0
            })`,
        mobileWeb: hasAddButton
          ? `calc(var(--mobileVh, 1vh) * 100 - 360px + ${
              isEmbeddedInMainApp ? '80px' : 0
            })`
          : `calc(var(--mobileVh, 1vh) * 100 - 300px + ${
              isEmbeddedInMainApp ? '80px' : 0
            })`,
        tablet: hasAddButton
          ? `calc(100vh - 362px + ${isEmbeddedInMainApp ? '80px' : 0})`
          : `calc(100vh - 302px + ${isEmbeddedInMainApp ? '80px' : 0})`,
        desktop: hasAddButton
          ? `calc(100vh - 362px + ${isEmbeddedInMainApp ? '80px' : 0})`
          : `calc(100vh - 302px + ${isEmbeddedInMainApp ? '80px' : 0})`,
      })};
  }
`;

export const TaskContainer = styled.div<{
  isMobileApp: boolean;
  hasAddButton: boolean;
}>`
  border-radius: 4px;
  margin-bottom: 12px;
`;

export const DropZone = styled.div<{
  scrollContainerHeight?: number;
}>``;

export const DoneCheckbox = styled(CircularCheckbox)`
  margin-right: 8px;
`;

export const SectionEmptyStateContainer = styled(Flex)`
  background: ${ThemeV2.palette.gray3};
  padding: 16px 0;
`;

export const AddNewTaskButton = styled(Flex)<{
  disabled?: boolean;
  sticky?: boolean;
}>`
  background: ${ThemeV2.palette.gray3};

  ${({ sticky }) => `${sticky && 'position: sticky'}`};

  flex-flow: row nowrap;
  bottom: 0;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  ${({ disabled }) => `${disabled && 'opacity: 0.5'}`};
  ${({ disabled }) => `${disabled && 'pointer-events: none'}`};
  ${({ disabled }) => `${disabled && 'padding: 8px 0 4px 0'}`};
  ${({ disabled }) => `${disabled && 'margin-top: 8px'}`};
`;

export const AddNewTaskButtonLabel = styled(Flex)`
  width: 100%;
  background: ${ThemeV2.palette.gray3};
  margin: 12px;
  padding: 8px 12px;

  &:hover {
    background: ${ThemeV2.palette.gray4};
    border-radius: 4px;
  }
`;

export const AddNewTaskIcon = styled.span`
  padding: 4px;

  &:hover {
    background: ${ThemeV2.palette.gray4};
    border-radius: 4px;
  }
`;

export const CreateTaskTextAreaWrapper = styled.div<{
  position?: TaskCreationPosition;
}>`
  textarea {
    background: ${ThemeV2.palette.gray1};
    border: 1px solid ${ThemeV2.palette.gray5};
    padding: 16px;
    height: auto;
    border-radius: 4px;

    &:focus {
      background: ${ThemeV2.palette.gray1};
      border: 1px solid ${ThemeV2.palette.gray5};
    }
  }
`;

export const SectionalContentWrapper = styled.div`
  min-height: 1px;
`;

export const EditableTitle = styled(NewTextfield)<{
  disabled?: boolean;
  color?: string;
  dummyTitle?: boolean;
}>`
  background: ${ThemeV2.palette.white};
  margin-bottom: 0;
  ${({ disabled }) => `${disabled && 'opacity: 0.5'}`};
  display: inline-flex;
  flex-grow: 1;

  &.MuiTextField-root {
    width: auto;
    ${({ dummyTitle }) => `${dummyTitle && `margin-right: 16px`}`};

    input {
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
    }
    ${({ color }) => `${color && `color: ${color}`}`};
  }

  .MuiOutlinedInput-input {
    padding: 8px;
    color: ${ThemeV2.palette.gray9};
  }
`;

export const UnassignedTitleText = styled(Body)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 8px;
  cursor: auto;
`;

export const TitleText = styled(UnassignedTitleText)`
  max-width: 220px;
  display: inline-flex;
  flex-grow: 1;
  width: 100%;

  &:hover {
    background: ${ThemeV2.palette.white};
    border: 0.5px solid ${ThemeV2.palette.gray5};
    // padding is 7px to account for the border and prevent jerk on hover
    padding: 8px 7px;
    border-radius: 4px;
    cursor: pointer;
  }
`;

export const Loader = styled(SVGIcon)`
  opacity: 0.5;
  animation: rotation 1s infinite;

  @keyframes rotation {
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const SectionMenu = styled(StyledDropdownIconButton)`
  display: inline-flex;
  margin-left: 4px;
  margin-right: 8px;
  button {
    height: 32px;
    width: 32px;
  }
`;

export const InfoTooltip = styled(Tooltip)`
  .tooltip-text {
    max-width: 180px;
  }

  &.section-title-tooltip {
    display: inline-flex;
    flex-grow: 1;
  }
`;

export const InfoTooltipWrapper = styled.div`
  display: inline-flex;
  flex-grow: 1;
`;

export const StyledInfiniteScroll = styled(InfiniteScroll)<{
  hasTasks?: boolean;
  isMobileApp: boolean;
  hasAddButton: boolean;
}>`
  & > :first-child {
    ${({ hasTasks }) => `${!hasTasks && `margin: 12px 0`}`};
    ${({ hasTasks }) => `${hasTasks && `margin-bottom: 12px`}`};
  }
`;
